// Coordinatore del client: tiene configurazione, scope, transport, log buffer,
// stats e dedup, e implementa la logica di cattura/invio. Specchio di
// `CloseYourIt::Client` (gem) e `CloseYourItClient` (dart). Dipendenze iniettabili
// per i test. Nessun metodo solleva.

import type { BreadcrumbInput } from './breadcrumbs'
import { Deduplicator, dedupKey } from './dedup'
import {
  buildErrorEvent,
  buildLogEvent,
  buildMessageEvent,
  normalizeError,
} from './events'
import {
  MAX_PAYLOAD_BYTES,
  capEvent,
  capGeneric,
  capReplayChunk,
  chunkLogBatch,
} from './budget'
import { LOGS_MAX_BATCH, LogBuffer } from './logBuffer'
import { meetsMinLevel, normalizeLogLevel } from './logLevel'
import { buildPerformanceIssueEvent, buildSlowMethodEvent } from './metricEvents'
import {
  disabledReason,
  endpointHost,
  eventsUri,
  isEnabled,
  logsUri,
  metricsUri,
  pageviewsUri,
  replayUri,
  resolveOptions,
  resolveTransportAuth,
  storeUri,
  webVitalsUri,
} from './options'
import { buildPageview } from './pageviews'
import { autoRequestContext } from './requestContext'
import type { ResolvedOptions, TransportAuth } from './options'
import { isBrowser, serverName as detectServerName } from './runtime'
import { createScopeStorage } from './asyncContext'
import type { ScopeStorage } from './asyncContext'
import { Diagnostics, diagnosticsSuppressed, errorName } from './diagnostics'
import { Scope } from './scope'
import { Stats } from './stats'
import {
  formatTraceparent,
  isSameOrigin,
  matchesPropagationTarget,
  normalizeBaggage,
  parseTraceparent,
  parseTracestate,
  readTraceHeader,
} from './traceContext'
import { Transport } from './transport'
import type {
  CaptureHint,
  CloseYourItOptions,
  DiagnosticDetails,
  DropReason,
  EventLevel,
  EventPayload,
  LogLevel,
  TraceCarrier,
  UserContext,
} from './types'
import { spanId, uuidV4 } from './uuid'

/// Dipendenze iniettabili (test).
export interface ClientDeps {
  transport?: Transport
  logBuffer?: LogBuffer
  stats?: Stats
  scope?: Scope
  dedup?: Deduplicator
  random?: () => number
  /// Provider del request context automatico (default: pagina corrente nel browser).
  requestProvider?: () => Record<string, unknown> | undefined
  /// Clock monotono in ms per `measure` (default `performance.now`).
  nowMs?: () => number
}

export class Client {
  readonly options: ResolvedOptions
  readonly stats: Stats
  /// Notificatore diagnostico process-local (hook `onDiagnostic`), non ricorsivo.
  readonly diagnostics: Diagnostics

  // Scope root: unico holder nel browser (single-thread) e fallback su Node fuori
  // da withScope/runWithScope. `scope` (getter) risolve la copia attiva del flusso.
  private readonly baseScope: Scope
  // Storage per-flusso su Node (AsyncLocalStorage); `undefined` nel browser (CYJS-7).
  private readonly als: ScopeStorage | undefined
  // Scope attivo nel fallback sincrono (browser, o Node senza AsyncLocalStorage).
  private syncScope: Scope | undefined

  private readonly transport: Transport
  /// Credenziale d'invio risolta per l'ambiente (bearer server / dsn browser).
  private readonly auth: TransportAuth
  private readonly logBuffer: LogBuffer
  private readonly dedup: Deduplicator
  private readonly random: () => number
  private readonly requestProvider: () => Record<string, unknown> | undefined
  /// Clock monotono in ms (iniettabile): usato da `measure` e dalle integrazioni.
  readonly nowMs: () => number
  /// Hostname del server, risolto una volta (non cambia nel processo): allegato
  /// come `server_name` a errori/messaggi. `undefined` nel browser o con
  /// `sendServerName: false` → il campo resta assente (parità con la gem Ruby).
  private readonly serverName: string | undefined
  // Lo `shutdown` diagnostico è emesso una sola volta per client (una close()
  // ripetuta non lo duplica), parità gem Ruby.
  private shutdownNotified = false
  // Esito del campionamento Web Vitals per QUESTO client, tirato una volta sola
  // (CYJS-28). Nel browser un client è un caricamento di pagina, e le sue misure
  // possono partire in più batch: vedi `captureWebVitals`.
  private webVitalsSampled: boolean | undefined

  constructor(rawOptions: CloseYourItOptions, deps: ClientDeps = {}) {
    this.options = resolveOptions(rawOptions)
    this.stats = deps.stats ?? new Stats()
    this.diagnostics = new Diagnostics(this.options.onDiagnostic)
    this.baseScope = deps.scope ?? new Scope(this.options.maxBreadcrumbs)
    this.als = createScopeStorage()
    this.dedup = deps.dedup ?? new Deduplicator()
    this.random = deps.random ?? Math.random
    this.requestProvider =
      deps.requestProvider ?? (() => autoRequestContext(this.options.sendPii))
    this.nowMs = deps.nowMs ?? defaultNowMs
    this.serverName = this.options.sendServerName ? detectServerName() : undefined

    // Nel browser è ammessa solo la DSN public key: il bearer segreto non è mai
    // eleggibile (fallback innocuo a bearer vuoto quando l'SDK è disabilitato →
    // il transport non viene comunque mai usato, i metodi gatano su `enabled`).
    this.auth = resolveTransportAuth(this.options, isBrowser()) ?? {
      kind: 'bearer',
      secret: '',
    }
    this.transport =
      deps.transport ??
      new Transport({
        auth: this.auth,
        stats: this.stats,
        maxQueue: this.options.maxQueue,
        debug: this.options.debug ? (m, e) => this.debugLog(m, e) : undefined,
        diagnostic: (event, details) => this.diagnostics.notify(event, details),
      })
    this.logBuffer =
      deps.logBuffer ??
      new LogBuffer(
        (batch) => this.flushLogs(batch),
        this.options.logsBatchSize,
        this.options.logsFlushIntervalMs,
      )

    // trace_id di pagina: correla errori e log della stessa sessione/pagina.
    if (this.scope.traceId === undefined) {
      this.scope.setTraceId(uuidV4().replace(/-/g, ''))
    }
  }

  /// Scope attivo per il flusso corrente: la copia isolata quando si è dentro
  /// withScope/runWithScope (per-richiesta su Node via AsyncLocalStorage),
  /// altrimenti lo scope root condiviso. Tutte le API di scope e la cattura
  /// eventi passano di qui, così ogni flusso vede solo i propri dati (CYJS-7).
  get scope(): Scope {
    return this.als?.getStore() ?? this.syncScope ?? this.baseScope
  }

  get enabled(): boolean {
    return isEnabled(this.options, isBrowser())
  }

  get disabledReason(): string | null {
    return disabledReason(this.options, isBrowser())
  }

  // Endpoint degli eventi errore/messaggio: col bearer è il path v1; con la DSN
  // public key (browser) è il drop-in Sentry `/store`, che il backend accetta
  // autenticando via `?sentry_key=`.
  private get eventsIngestUri(): string {
    return this.auth.kind === 'dsn' ? storeUri(this.options) : eventsUri(this.options)
  }

  get endpointHost(): string | undefined {
    return endpointHost(this.options)
  }

  get slowExternalThresholdMs(): number {
    return this.options.slowExternalThresholdMs
  }

  // ---------------------------------------------------------------- errori

  /// Cattura un'eccezione (o valore arbitrario). No-op se disabilitato, escluso,
  /// già catturato, duplicato entro la finestra, o non campionato.
  captureException(error: unknown, hint: CaptureHint = {}): void {
    if (!this.enabled || diagnosticsSuppressed()) return

    const normalized = normalizeError(error)
    if (this.options.excludedExceptions.includes(normalized.type)) return
    if (this.dedup.wasCaptured(error)) return

    const key = dedupKey(
      normalized.type,
      normalized.value,
      topFrameKey(normalized.stack),
    )
    if (this.dedup.isDuplicate(key)) return
    // Marcato PRIMA del sampling (parità gem): un errore scartato dal sampling
    // non viene re-inviato da un handler più esterno.
    this.dedup.markCaptured(error)
    if (!this.sampled(this.options.sampleRate)) {
      this.noteDrop('sample_rate')
      return
    }

    const payload = buildErrorEvent({
      error: normalized,
      environment: this.options.environment,
      release: this.options.release,
      handled: hint.handled ?? false,
      level: hint.level ?? 'error',
      scopeHash: this.scopeHash(),
      traceId: this.scope.traceId,
      replayId: this.scope.replaySessionId,
      contexts: hint.contexts,
      request: this.autoRequest(),
      serverName: this.serverName,
    })
    this.enqueue(payload, this.eventsIngestUri, capEventToBudget)
  }

  /// Cattura un messaggio diagnostico.
  captureMessage(message: string, level: EventLevel = 'info'): void {
    if (!this.enabled || diagnosticsSuppressed()) return
    if (!this.sampled(this.options.sampleRate)) {
      this.noteDrop('sample_rate')
      return
    }

    const payload = buildMessageEvent({
      message,
      environment: this.options.environment,
      release: this.options.release,
      level,
      scopeHash: this.scopeHash(),
      traceId: this.scope.traceId,
      replayId: this.scope.replaySessionId,
      request: this.autoRequest(),
      serverName: this.serverName,
    })
    this.enqueue(payload, this.eventsIngestUri, capEventToBudget)
  }

  // ------------------------------------------------------------------- logs

  /// Bufferizza un log strutturato (inviato in batch a `/logs`). No-op se i log
  /// sono disattivati, sotto `logsMinLevel`, non campionati o con message vuoto.
  log(
    level: LogLevel,
    message: string,
    attributes?: Record<string, unknown>,
    logger?: string,
  ): void {
    if (!this.enabled || !this.options.logsEnabled || diagnosticsSuppressed()) return
    const normalized = normalizeLogLevel(level)
    if (!meetsMinLevel(normalized, this.options.logsMinLevel)) return
    if (!this.sampled(this.options.logsSampleRate)) {
      this.noteDrop('sample_rate')
      return
    }
    if (message.trim().length === 0) return

    this.logBuffer.add(
      buildLogEvent({
        message,
        level: normalized,
        environment: this.options.environment,
        release: this.options.release,
        logger,
        traceId: this.scope.traceId,
        attributes,
      }),
    )
  }

  // --------------------------------------------------------------- metriche

  /// Misura [body] e invia una metrica `slow_method` se la durata supera
  /// `slowMethodThresholdMs`. Preserva la sincronicità: un body sincrono
  /// ritorna il valore, un body async ritorna la Promise (misurata al settle).
  /// La metrica parte anche se il body lancia. MAI campionata.
  /// Label vuota → TypeError immediato anche a client disabilitato (errore del
  /// programmatore, deve emergere deterministicamente).
  measure<T>(
    label: string,
    body: () => T,
    opts: { args?: Record<string, unknown> } = {},
  ): T {
    if (typeof label !== 'string' || label.trim().length === 0) {
      throw new TypeError(
        'closeyourit: measure(label) richiede una label non vuota (è il fingerprint della metrica)',
      )
    }

    const start = this.nowMs()
    const finish = (): void => {
      const duration = this.nowMs() - start
      this.recordSlowMethod(label, duration, opts.args)
    }

    try {
      const result = body()
      if (isThenable(result)) {
        return (result as Promise<unknown>).then(
          (value) => {
            finish()
            return value
          },
          (error: unknown) => {
            finish()
            throw error
          },
        ) as T
      }
      finish()
      return result
    } catch (error) {
      finish()
      throw error
    }
  }

  /// Verdetto `slow_external_http` per una richiesta esterna completata.
  /// No-op sotto soglia o senza `detectPerformanceIssues`/`captureExternalHttp`.
  recordExternalHttp(host: string, url: string, durationMs: number): void {
    if (!this.perfEnabled() || !this.options.captureExternalHttp) return
    const threshold = this.options.slowExternalThresholdMs
    if (threshold <= 0 || durationMs < threshold) return

    this.enqueue(
      buildPerformanceIssueEvent({
        subtype: 'slow_external_http',
        durationMs,
        environment: this.options.environment,
        httpHost: host,
        httpUrl: url,
        traceId: this.scope.traceId,
      }),
      metricsUri(this.options),
      capGenericToBudget,
    )
  }

  /// Verdetto `repeated_http`: stessa richiesta ripetuta [count] volte nella
  /// finestra (l'N+1 del client). Il tracking della finestra sta all'integrazione.
  recordRepeatedHttp(host: string, url: string, count: number): void {
    if (!this.perfEnabled() || !this.options.captureExternalHttp) return

    this.enqueue(
      buildPerformanceIssueEvent({
        subtype: 'repeated_http',
        environment: this.options.environment,
        httpHost: host,
        httpUrl: url,
        queryCount: count,
        traceId: this.scope.traceId,
      }),
      metricsUri(this.options),
      capGenericToBudget,
    )
  }

  /// Verdetto `jank`: long task del main thread oltre soglia.
  recordJank(durationMs: number, route: string): void {
    if (!this.perfEnabled() || !this.options.detectJank) return

    this.enqueue(
      buildPerformanceIssueEvent({
        subtype: 'jank',
        durationMs,
        environment: this.options.environment,
        route,
        traceId: this.scope.traceId,
      }),
      metricsUri(this.options),
      capGenericToBudget,
    )
  }

  // --------------------------------------------------------------- pageview

  /// Cattura una pageview (web analytics cookieless). No-op se disabilitato o
  /// fuori dal browser. MAI campionata (parità con metriche/log: l'analytics
  /// perde di senso con dati parziali). [overrides.path] forza il path (default
  /// `location.pathname`); `environment` è aggiunto qui dall'ambiente risolto.
  /// Passa da `enqueue`, quindi rispetta `beforeSend` e i contatori come gli
  /// altri invii.
  capturePageview(overrides: { path?: string } = {}): void {
    if (!this.enabled) return
    if (!isBrowser()) return

    this.enqueue(
      { ...buildPageview(overrides), environment: this.options.environment },
      pageviewsUri(this.options),
      capGenericToBudget,
    )
  }

  /// Cattura un custom event (goal/conversione). [name] è il nome dell'evento
  /// (mai "pageview"); no-op se disabilitato, fuori dal browser o senza nome.
  /// Stessa location/hostname/UTM del contesto corrente della pageview.
  captureEvent(name: string, overrides: { path?: string } = {}): void {
    if (!this.enabled) return
    if (!isBrowser()) return
    if (!name) return

    this.enqueue(
      { ...buildPageview(overrides), name, environment: this.options.environment },
      pageviewsUri(this.options),
      capGenericToBudget,
    )
  }

  // ------------------------------------------------------------ web vitals

  /// Invia le misure Web Vitals della pagina (LCP/CLS/INP/TTFB/FCP) in **un solo
  /// invio**: il backend accetta un array e un caricamento produce una manciata
  /// di misure, che al momento in cui la pagina esce di scena conviene far
  /// partire insieme. No-op se disabilitato, fuori dal browser o senza misure.
  ///
  /// Il campionamento (`webVitalsSampleRate`) decide sul **caricamento intero**,
  /// non sul singolo batch: le misure di una pagina partono tutte o nessuna,
  /// anche quando arrivano in due invii (il flush di cortesia, CYJS-28). Tirare i
  /// dadi a ogni batch costruirebbe i percentili di metriche diverse su insiemi
  /// di pagine diversi, che non sono più confrontabili tra loro. Uno scarto vale
  /// un `sample_rate`.
  /// `environment` e `release` sono aggiunti qui (li conosce solo il client).
  captureWebVitals(measures: EventPayload[]): void {
    if (!this.enabled) return
    if (!isBrowser()) return
    if (measures.length === 0) return
    if (diagnosticsSuppressed()) return
    if (!this.sampledForWebVitals()) {
      this.noteDrop('sample_rate')
      return
    }

    const stamped = measures.map((measure) => ({
      ...measure,
      environment: this.options.environment,
      ...(this.options.release ? { release: this.options.release } : {}),
    }))
    const kept = this.applyBeforeSend(stamped).map(capGenericToBudget)
    if (kept.length === 0) return

    const url = webVitalsUri(this.options)
    if (this.transport.send(kept, url)) {
      this.stats.increment('enqueued')
      this.diagnostics.notify('enqueue', { path: pathOf(url), batch: kept.length })
    }
  }

  // ----------------------------------------------------------------- replay

  /// Imposta l'id della sessione di replay corrente (lo mette sullo scope → gli
  /// errori successivi lo portano in `contexts.replay.replay_id`). Chiamato dal
  /// recorder all'avvio.
  setReplaySessionId(id: string | undefined): void {
    this.scope.setReplaySessionId(id)
  }

  /// Invia un chunk di replay (già costruito da `buildReplayChunk`). No-op se
  /// disabilitato o fuori dal browser. Passa da `enqueue` → rispetta `beforeSend`
  /// e i contatori come gli altri invii. `environment` aggiunto qui.
  captureReplayChunk(chunk: EventPayload): void {
    if (!this.enabled) return
    if (!isBrowser()) return

    this.enqueue(
      { ...chunk, environment: this.options.environment },
      replayUri(this.options),
      capReplayChunkToBudget,
    )
  }

  // ------------------------------------------------------------------ scope

  setUser(user: UserContext | null): void {
    this.scope.setUser(user)
  }

  setTag(key: string, value: string): void {
    this.scope.setTag(key, value)
  }

  setTags(tags: Record<string, string>): void {
    this.scope.setTags(tags)
  }

  setExtra(key: string, value: unknown): void {
    this.scope.setExtra(key, value)
  }

  setContext(key: string, value: Record<string, unknown>): void {
    this.scope.setContext(key, value)
  }

  setTraceId(id: string | undefined): void {
    this.scope.setTraceId(id)
  }

  addBreadcrumb(input: BreadcrumbInput): void {
    if (!this.options.breadcrumbs) return
    this.scope.addBreadcrumb(input)
  }

  clearScope(): void {
    this.scope.clear()
  }

  // --------------------------------------------------------- trace context W3C

  /// Adotta un trace context W3C in ingresso da [carrier] (header di richiesta
  /// server/SSR, un `Headers`, o un getter): se `traceparent` è valido, il suo
  /// trace-id diventa il `trace_id` di correlazione — così errori/log/metriche si
  /// agganciano al trace distribuito — e parent/sampling/tracestate/baggage sono
  /// conservati per la ri-propagazione verso host fidati. Header assente o
  /// malformato: no-op (si mantiene il trace corrente). No-op se disabilitato.
  /// Dentro `runWithScope` opera sullo scope isolato della richiesta.
  continueTrace(carrier: TraceCarrier): void {
    if (!this.enabled) return
    const parsed = parseTraceparent(readTraceHeader(carrier, 'traceparent'))
    if (!parsed) return
    this.scope.continueTrace({
      traceId: parsed.traceId,
      parentId: parsed.parentId,
      sampled: parsed.sampled,
      tracestate: parseTracestate(readTraceHeader(carrier, 'tracestate')),
      baggage: normalizeBaggage(readTraceHeader(carrier, 'baggage')),
    })
  }

  /// Header W3C Trace Context da iniettare in una richiesta in uscita verso
  /// [target]: `traceparent` (trace-id corrente + nuovo span-id + flag di
  /// sampling), più `tracestate`/`baggage` se adottati. Ritorna `{}` — nessun
  /// header — se l'SDK è disabilitato o se [target] non è fidato
  /// (`tracePropagationTargets` o same-origin nel browser): così il trace interno
  /// e il baggage (potenziale PII) non raggiungono host non fidati. Senza
  /// [target] la destinazione è considerata first-party e la propagazione è
  /// consentita. Ogni chiamata genera un nuovo span-id (l'hop corrente).
  getTraceHeaders(target?: string): Record<string, string> {
    if (!this.enabled) return {}
    const traceId = this.scope.traceId
    if (traceId === undefined) return {}
    if (target !== undefined && !this.isPropagationAllowed(target)) return {}

    const headers: Record<string, string> = {
      traceparent: formatTraceparent(
        traceId,
        spanId(),
        this.scope.traceSampled ?? true,
      ),
    }
    if (this.scope.traceState) headers['tracestate'] = this.scope.traceState
    if (this.scope.traceBaggage) headers['baggage'] = this.scope.traceBaggage
    return headers
  }

  // Destinazione fidata: match nell'allowlist `tracePropagationTargets`, oppure
  // same-origin nel browser (la propria origine è first-party). Su Node manca
  // `location`: conta solo l'allowlist.
  private isPropagationAllowed(target: string): boolean {
    if (matchesPropagationTarget(target, this.options.tracePropagationTargets)) {
      return true
    }
    const origin = typeof location !== 'undefined' ? location.origin : undefined
    return origin !== undefined && isSameOrigin(target, origin)
  }

  /// Esegue [fn] in uno scope isolato per l'intero flusso asincrono: nuovo
  /// trace_id (unità di correlazione per-richiesta), breadcrumb propri e una copia
  /// di user/tag/extra/contexts correnti come base. Su Node l'isolamento è reale
  /// anche tra richieste concorrenti (AsyncLocalStorage): quel che [fn] imposta —
  /// e ogni sua continuazione `await` — non filtra in altri flussi (CYJS-7). Nel
  /// browser (single-thread) il fork è attivo per la sola durata sincrona di [fn].
  /// Da avvolgere attorno all'handler di richiesta (es. middleware server).
  /// Ritorna il valore di [fn]; una Promise è propagata (l'isolamento copre l'await).
  runWithScope<T>(fn: (scope: Scope) => T): T {
    const forked = this.scope.fork({ freshTrace: true })
    forked.setTraceId(uuidV4().replace(/-/g, ''))
    return this.runInScope(forked, fn)
  }

  /// Esegue [fn] con uno scope temporaneo forkato da quello corrente (trace_id e
  /// breadcrumb ereditati): le modifiche fatte dentro non escono. Utile per una
  /// cattura con contesto aggiuntivo senza sporcare lo scope condiviso.
  withScope<T>(fn: (scope: Scope) => T): T {
    return this.runInScope(this.scope.fork(), fn)
  }

  // Attiva [scope] per la durata di [fn]. Su Node l'AsyncLocalStorage propaga
  // l'isolamento attraverso i flussi asincroni e ripristina da solo il contesto
  // precedente (anche in caso di eccezione). Nel fallback sincrono si salva/ripristina
  // manualmente `syncScope` — corretto perché il browser non ha concorrenza reale.
  private runInScope<T>(scope: Scope, fn: (scope: Scope) => T): T {
    const als = this.als
    if (als) return als.run(scope, () => fn(scope))
    const previous = this.syncScope
    this.syncScope = scope
    try {
      return fn(scope)
    } finally {
      this.syncScope = previous
    }
  }

  // ------------------------------------------------------------- lifecycle

  /// Segnala al transport che la pagina sta uscendo di scena (`pagehide`,
  /// `visibilitychange(hidden)`) o vi è rientrata (`pageshow` da bfcache). Da
  /// pagina in uscita gli invii browser passano dal beacon, l'unico che il
  /// browser porta a termine dopo la chiusura della scheda (CYJS-29). Lo chiama
  /// il lifecycle del facade, non l'applicazione.
  notifyPageHiding(hiding: boolean): void {
    this.transport.setPageHiding(hiding)
  }

  /// Svuota i log bufferizzati e attende gli invii in volo.
  async flush(): Promise<void> {
    this.logBuffer.flush()
    await this.transport.flush()
  }

  /// Spegne il client: svuota i log, attende gli invii, poi notifica lo
  /// `shutdown` con lo snapshot dei contatori così l'operatore vede il bilancio
  /// dell'invio. Emesso DOPO il drain (e una sola volta): lo snapshot riflette
  /// anche i log dell'ultimo flush e gli invii completati, e nessun altro evento
  /// diagnostico lo segue (parità gem Ruby, CYJS-9).
  async close(): Promise<void> {
    this.logBuffer.dispose()
    await this.transport.flush()
    if (!this.shutdownNotified) {
      this.shutdownNotified = true
      this.diagnostics.notify('shutdown', { stats: this.stats.toObject() })
    }
  }

  // -------------------------------------------------------------- internals

  // Registra uno scarto client-side col suo motivo e lo notifica all'hook: unico
  // punto che tiene allineati il contatore (`reasons`) e la callback (CYJS-9).
  private noteDrop(reason: DropReason, details: DiagnosticDetails = {}): void {
    this.stats.record(reason)
    this.diagnostics.notify('drop', { reason, ...details })
  }

  // [cap] applica il budget byte del canale (CYJS-10) DOPO `beforeSend`: l'hook
  // può gonfiare il payload, quindi il cap è l'ultima cosa prima del filo, così
  // ciò che parte rispetta sempre il limite del servizio.
  private enqueue(
    payload: EventPayload,
    url: string,
    cap?: (payload: EventPayload) => EventPayload,
  ): void {
    // Un evento generato DALLA callback diagnostica non deve produrre telemetria
    // (né toccare i contatori): la diagnostica è a somma zero (CYJS-9).
    if (diagnosticsSuppressed()) return

    const hook = this.options.beforeSend
    let body: EventPayload = payload
    if (hook) {
      let result: EventPayload | null
      try {
        result = hook(payload)
      } catch (error) {
        // beforeSend non deve mai far crashare l'host: assorbito e reso visibile
        // come scarto col motivo `error` (parità gem Ruby).
        this.noteDrop('error', { error: errorName(error) })
        this.debugLog('beforeSend ha sollevato, evento scartato', error)
        return
      }
      if (result === null) {
        this.noteDrop('before_send')
        return
      }
      body = result
    }
    if (cap) body = cap(body)
    if (this.transport.send(body, url)) {
      this.stats.increment('enqueued')
      this.diagnostics.notify('enqueue', { path: pathOf(url) })
    }
  }

  // `beforeSend` applicato a ogni elemento di un batch: una voce scartata (o che
  // fa sollevare l'hook) non porta via le altre, ed è contata col suo motivo.
  // Senza hook il batch passa intatto. Condiviso da log e web vitals: sono gli
  // unici due canali che spediscono più payload in una richiesta sola.
  private applyBeforeSend(batch: EventPayload[]): EventPayload[] {
    const hook = this.options.beforeSend
    if (!hook) return batch

    const kept: EventPayload[] = []
    for (const event of batch) {
      let result: EventPayload | null
      try {
        result = hook(event)
      } catch (error) {
        this.noteDrop('error', { error: errorName(error) })
        continue
      }
      if (result === null) {
        this.noteDrop('before_send')
        continue
      }
      kept.push(result)
    }
    return kept
  }

  private flushLogs(batch: EventPayload[]): void {
    if (batch.length === 0) return
    const payloads = this.applyBeforeSend(batch)
    if (payloads.length === 0) return
    // Il backend rifiuta batch oltre LOGS_MAX_BATCH (413) e i payload troppo
    // grandi: chunkLogBatch spezza sia per conteggio sia per byte, troncando i
    // singoli log giganti, senza mai perdere una voce (CYJS-10).
    const url = logsUri(this.options)
    for (const chunk of chunkLogBatch(payloads, MAX_PAYLOAD_BYTES, LOGS_MAX_BATCH)) {
      if (this.transport.send(chunk, url)) {
        this.stats.increment('enqueued')
        this.diagnostics.notify('enqueue', { path: pathOf(url), batch: chunk.length })
      }
    }
  }

  private recordSlowMethod(
    label: string,
    durationMs: number,
    args: Record<string, unknown> | undefined,
  ): void {
    if (!this.enabled) return
    if (durationMs < this.options.slowMethodThresholdMs) return

    this.enqueue(
      buildSlowMethodEvent({
        label,
        durationMs,
        environment: this.options.environment,
        traceId: this.scope.traceId,
        args: this.options.captureMethodArguments ? args : undefined,
      }),
      metricsUri(this.options),
      capGenericToBudget,
    )
  }

  private perfEnabled(): boolean {
    return this.enabled && this.options.detectPerformanceIssues
  }

  private scopeHash(): EventPayload {
    return this.scope.toEventHash({ sendPii: this.options.sendPii })
  }

  private autoRequest(): Record<string, unknown> | undefined {
    if (!this.options.captureRequest) return undefined
    return this.requestProvider()
  }

  private sampled(rate: number): boolean {
    if (rate >= 1) return true
    if (rate <= 0) return false
    return this.random() < rate
  }

  // Il dado dei Web Vitals si tira una volta per client e poi vale per sempre:
  // il caricamento è dentro o fuori, non a metà. Il valore memoizzato è ciò che
  // tiene insieme il primo invio e quelli del flush di cortesia.
  private sampledForWebVitals(): boolean {
    this.webVitalsSampled ??= this.sampled(this.options.webVitalsSampleRate)
    return this.webVitalsSampled
  }

  // Log diagnostici interni su console (mai nel log buffer: eviterebbe loop di
  // telemetria su un invio fallito). Attivo solo con `debug: true`.
  private debugLog(message: string, error?: unknown): void {
    if (!this.options.debug) return
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug('[closeyourit]', message, error ?? '')
    }
  }
}

// Cap dei canali eventi e replay ancorati al budget conservativo condiviso
// (CYJS-10): applicati in `enqueue` come ultimo passo prima dell'invio.
function capEventToBudget(payload: EventPayload): EventPayload {
  return capEvent(payload, MAX_PAYLOAD_BYTES)
}

function capReplayChunkToBudget(payload: EventPayload): EventPayload {
  return capReplayChunk(payload, MAX_PAYLOAD_BYTES)
}

// Metriche e pageview: label/url/route/path/referrer/utm (e le iniezioni di
// beforeSend) non hanno un tetto proprio, il cap generico li tiene sotto budget.
function capGenericToBudget(payload: EventPayload): EventPayload {
  return capGeneric(payload, MAX_PAYLOAD_BYTES)
}

// Path dell'endpoint (senza host né query): metadato non sensibile allegato
// all'evento diagnostico `enqueue` (mai l'URL completo).
function pathOf(url: string): string | undefined {
  try {
    return new URL(url).pathname
  } catch {
    return undefined
  }
}

function isThenable(value: unknown): value is Promise<unknown> {
  return (
    value !== null &&
    (typeof value === 'object' || typeof value === 'function') &&
    typeof (value as { then?: unknown }).then === 'function'
  )
}

function defaultNowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

// Prima riga di stack utile (crash frame) per la chiave di dedup.
function topFrameKey(stack: string | undefined): string | undefined {
  if (!stack) return undefined
  for (const raw of stack.split('\n')) {
    const line = raw.trim()
    if (line.startsWith('at ') || line.includes('@')) return line
  }
  return undefined
}
